import { AlertCircle, Info } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { SectorMismatchResult } from "@/utils/sectorMismatch";

type AutoSeedConfirmDialogProps = {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  sectorNames: string[];
  source: "decizie" | "notificare";
  mismatch: SectorMismatchResult | null;
};

const SOURCE_LABELS = {
  decizie: "Decizia de Inregistrare",
  notificare: "Formularul de Notificare",
} as const;

export default function AutoSeedConfirmDialog({
  open,
  onConfirm,
  onCancel,
  sectorNames,
  source,
  mismatch,
}: AutoSeedConfirmDialogProps) {
  const hasNoSectors = sectorNames.length === 0;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sectoare pentru Analiza de Risc</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {hasNoSectors ? (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <AlertDescription className="text-xs">
                    Nu s-au gasit sectoare corespunzatoare in baza de date.
                    Adaugati manual sectoarele dorite.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Urmatoarele sectoare vor fi adaugate automat din{" "}
                    <span className="font-medium text-foreground">
                      {SOURCE_LABELS[source]}
                    </span>
                    :
                  </p>
                  <ul className="space-y-1">
                    {sectorNames.map((name) => (
                      <li
                        key={name}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        {name}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {mismatch?.hasMismatch && (
                <Alert variant="warning">
                  <Info className="h-4 w-4 mt-0.5" />
                  <AlertDescription className="text-xs space-y-1">
                    <p className="font-medium">
                      Sectoarele din Decizie difera de cele din Notificare.
                      Decizia are prioritate.
                    </p>
                    {mismatch.onlyInDecizie.length > 0 && (
                      <p>
                        Doar in Decizie: {mismatch.onlyInDecizie.join(", ")}
                      </p>
                    )}
                    {mismatch.onlyInNotificare.length > 0 && (
                      <p>
                        Doar in Notificare:{" "}
                        {mismatch.onlyInNotificare.join(", ")}
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Anuleaza</AlertDialogCancel>
          {!hasNoSectors && (
            <AlertDialogAction onClick={onConfirm}>Confirma</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
